import { type ReactElement, useMemo, useState } from "react";
import { type FilterData } from "~/features/cms/components/cmsFilterableTile/CMSFilterableTileContainer";
import { trackFilterToggle } from "~/app/analytics/ga/contentStream";
import { type CMSFilterableTileContainerFilterDTO } from "~/features/cms/components/cmsFilterableTile/dto";
import { cleanupText } from "~/features/cms/utils/markup";
import classNames from "classnames";
import { styled } from "@schaeffler-commerce/design-system";
import { C_ON_SURFACE, C_OUTLINE_VARIANT } from "@schaeffler-commerce/design-system";
import CheckIcon from "@mui/icons-material/Check";

export interface FilterTileProps extends Omit<CMSFilterableTileContainerFilterDTO, "type" | "tags"> {
  className?: string;
  active: boolean;
  onClick: () => void;
}

interface Props<T extends { tags?: string }> {
  allTiles: T[];
  showAllLabel?: string;
  /** True when a title element immediately precedes the filters (mirrors the
   *  LESS `__title + __filters` sibling rule migrated from
   *  CMSFilterableTileContainer.less). */
  precededByTitle?: boolean;

  filters: CMSFilterableTileContainerFilterDTO[];
}

export function useTileFilter<T extends { tags?: string }>({
  filters,
  allTiles,
  showAllLabel,
  precededByTitle = false,
}: Props<T>) {
  const [activeFilters, setActiveFilters] = useState<FilterData[]>([]);

  const { clear, toggle } = useMemo(
    () => ({
      clear: () => setActiveFilters([]),
      toggle: (filter: FilterData) =>
        setActiveFilters((prev) => {
          let newFilters = prev.filter((item) => item != filter);
          if (prev.length === newFilters.length) {
            newFilters = prev.concat(filter);
          }

          trackFilterToggle(newFilters);

          return newFilters;
        }),
    }),
    [],
  );

  const filterTiles = useMemo(() => {
    const filterTiles: ReactElement<FilterTileProps>[] = [];

    if (showAllLabel) {
      filterTiles.push(
        <FilterTile key={"all"} label={showAllLabel} onClick={clear} active={activeFilters.length === 0} />,
      );
    }

    filters.forEach((filter, index) => {
      filterTiles.push(
        <FilterTile
          key={index}
          label={filter.label}
          onClick={() => toggle(filter)}
          active={activeFilters.includes(filter)}
        />,
      );
    });

    return filterTiles;
  }, [activeFilters, filters, showAllLabel, toggle, clear]);

  const visibleTiles = useMemo(() => {
    let visibleTiles;
    if (activeFilters.length === 0) {
      visibleTiles = allTiles.map((t, index) => ({ ...t, index }));
    } else {
      const visibleTags = new Set(
        activeFilters
          .map((filter) => filter.tags ?? "")
          .map((str) => str.split(","))
          .flat(),
      );

      visibleTiles = allTiles
        .map((t, index) => ({ ...t, index }))
        .filter((tile) => (tile.tags ?? "").split(",").some((tag) => visibleTags.has(tag)));
    }

    return visibleTiles;
  }, [activeFilters, allTiles]);

  return {
    visibleTiles,
    filterContainer: <FilterContainer filters={filterTiles} precededByTitle={precededByTitle} />,
  };
}

interface FilterContainerProps {
  className?: string;
  filters: ReactElement<unknown>[];
  precededByTitle?: boolean;
}

// Migrated from CMSFilterableTileContainer.less (__filters): the mobile
// full-bleed row (negative left offset by the 16px container side padding)
// collapses back to the container at desktop-s. The title sibling margin is
// applied through the precededByTitle prop to preserve the LESS
// `__title + __filters` rule exactly.
const Filters = styled("div", { shouldForwardProp: (prop) => prop !== "precededByTitle" })<
  React.ComponentProps<"div"> & { precededByTitle?: boolean }
>(({ theme, precededByTitle }) => ({
  ...(precededByTitle && {
    marginTop: "16px",
    [theme.breakpoints.up("desktop-s")]: { marginTop: "24px" },
  }),
  marginBottom: "24px",
  [theme.breakpoints.up("desktop-s")]: { marginBottom: "32px" },
  position: "relative",
  left: "-16px",
  paddingLeft: "16px",
  overflowX: "auto",
  whiteSpace: "pre",
  width: "100vw",
  [theme.breakpoints.up("desktop-s")]: {
    left: 0,
    paddingLeft: 0,
    width: "100%",
    overflow: "hidden",
  },
}));

// Migrated from CMSFilterableTileContainer.less (__filters__row).
const FiltersRow = styled("div")({
  display: "flex",
  flexFlow: "wrap",
  gap: "8px",
});

const FilterContainer = ({ filters, className, precededByTitle }: FilterContainerProps) => {
  if (!filters.length) {
    return null;
  }

  return (
    <Filters className={className} precededByTitle={precededByTitle}>
      <FiltersRow>{filters}</FiltersRow>
    </Filters>
  );
};

// Migrated from CMSFilterableTileContainer.less (__filter/--active), with the
// .rounded-card mixin expanded (border-radius 8px, 1px outline-variant border).
const FilterTileRoot = styled("div", { shouldForwardProp: (prop) => prop !== "active" })<
  React.ComponentProps<"div"> & { active?: boolean }
>(({ active }) => ({
  borderRadius: "8px",
  border: `1px solid ${C_OUTLINE_VARIANT}`,
  cursor: "pointer",
  padding: "3px 8px 4px 8px",
  color: C_ON_SURFACE,
  display: "flex",
  alignItems: "center",
  ...(active && { background: C_ON_SURFACE, color: "white" }),
}));

const FilterTile = ({ className, active, onClick, label }: FilterTileProps) => {
  label = cleanupText(label);

  return (
    <FilterTileRoot className={classNames("copy_left", className)} active={active} onClick={onClick}>
      {active && <CheckIcon className={"icon-margin"} />}
      {label}
    </FilterTileRoot>
  );
};
